<template>
  <div class="pointer-events-auto flex flex-row-reverse rounded-[4px] bg-white text-type-secondary">
    <gv-tooltip
      :label="`${map.panControlsExpanded ? 'Collapse' : 'Expand'} pan controls`"
      :floating-ui-options="{
        offset: 16,
        placement: 'left',
      }"
    >
      <button
        type="button"
        :aria-expanded="map.panControlsExpanded"
        class="flex size-8 items-center justify-center rounded-r-[4px] transition-colors"
        :class="{
          'bg-primary-400 fill-white': map.panControlsExpanded,
        }"
        @click="map.panControlsExpanded = !map.panControlsExpanded"
      >
        <gv-icon name="open_with" size="xs" aria-label="Pan up" />
      </button>
    </gv-tooltip>

    <div
      :aria-hidden="!map.panControlsExpanded"
      class="flex flex-row-reverse overflow-hidden ease-in-out"
      :class="{ hidden: !map.panControlsExpanded }"
    >
      <gv-tooltip
        label="Pan right"
        :floating-ui-options="{
          offset: 16,
          placement: 'top',
        }"
      >
        <button
          type="button"
          :class="[buttonTheme]"
          @pointerdown.capture="setOffset(Direction.RIGHT)"
          @pointerup="clearOffset"
          @keydown.space="setOffset(Direction.RIGHT)"
          @keyup.space="clearOffset"
          @keydown.enter="setOffset(Direction.RIGHT)"
          @keyup.enter="clearOffset"
          @keyup="clearOffset"
        >
          <gv-icon name="chevron_right" size="xs" aria-label="Pan right" />
        </button>
      </gv-tooltip>
      <gv-tooltip
        label="Pan left"
        :floating-ui-options="{
          offset: 16,
          placement: 'top',
        }"
      >
        <button
          type="button"
          :class="[buttonTheme]"
          @pointerdown.capture="setOffset(Direction.LEFT)"
          @pointerup="clearOffset"
          @keydown.space="setOffset(Direction.LEFT)"
          @keyup.space="clearOffset"
          @keydown.enter="setOffset(Direction.LEFT)"
          @keyup.enter="clearOffset"
          @keyup="clearOffset"
        >
          <gv-icon name="chevron_left" size="xs" aria-label="Pan left" />
        </button>
      </gv-tooltip>
      <gv-tooltip
        label="Pan down"
        :floating-ui-options="{
          offset: 16,
          placement: 'top',
        }"
      >
        <button
          type="button"
          :class="[buttonTheme]"
          @pointerdown.capture="setOffset(Direction.DOWN)"
          @pointerup="clearOffset"
          @keydown.space="setOffset(Direction.DOWN)"
          @keyup.space="clearOffset"
          @keydown.enter="setOffset(Direction.DOWN)"
          @keyup.enter="clearOffset"
          @keyup="clearOffset"
        >
          <gv-icon name="expand_more" size="xs" aria-label="Pan down" />
        </button>
      </gv-tooltip>
      <gv-tooltip
        label="Pan up"
        :floating-ui-options="{
          offset: 16,
          placement: 'top',
        }"
      >
        <button
          type="button"
          class="rounded-l-[4px]"
          :class="[buttonTheme]"
          @pointerdown.capture="setOffset(Direction.UP)"
          @pointerup="clearOffset"
          @keydown.space="setOffset(Direction.UP)"
          @keyup.space="clearOffset"
          @keydown.enter="setOffset(Direction.UP)"
          @keyup.enter="clearOffset"
          @keyup="clearOffset"
        >
          <gv-icon name="expand_less" size="xs" aria-label="Pan up" />
        </button>
      </gv-tooltip>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useMapStore } from '@/features/hazards';
import { ref, watch } from 'vue';

enum Direction {
  UP = 'up',
  DOWN = 'down',
  LEFT = 'left',
  RIGHT = 'right',
}

const DISTANCE = 200;
const DURATION = 300;

const buttonTheme =
  'transition-colors outline-offset-[-3px]  h-8 w-8 flex items-center justify-center hover:bg-grey-50 active:bg-primary-400 active:fill-white';

const map = useMapStore();

let panningInterval: ReturnType<typeof setInterval>;

const offset = ref<[number, number]>();

const setOffset = (direction: Direction) => {
  const offsetByDirection: Record<Direction, [number, number]> = {
    [Direction.UP]: [0, -DISTANCE],
    [Direction.DOWN]: [0, DISTANCE],
    [Direction.LEFT]: [-DISTANCE, 0],
    [Direction.RIGHT]: [DISTANCE, 0],
  };

  offset.value = offsetByDirection[direction];
};

const clearOffset = () => {
  offset.value = undefined;
};

const panByOffset = () => {
  if (!offset.value) return;
  map.mapbox?.panBy(offset.value, {
    duration: DURATION,
    easing: (time) => time,
  });
};

const startPanning = () => {
  panByOffset();
  panningInterval = setInterval(() => {
    panByOffset();
  }, DURATION);
};

const stopPanning = () => {
  clearInterval(panningInterval);
  map.mapbox?.stop();
};

watch(offset, () => {
  if (offset.value) {
    startPanning();
  } else {
    stopPanning();
  }
});
</script>
